// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  ICertificationWebpageGeneralProofGetListAttrs,
  ICertificationWebpageGeneralProofUpdateAttrs,
  ICertificationWebpageGeneralProofUploadDocumentAttrs,
  ICertificationWebpageGeneralProofDeleteDocumentAttrs,
  ICertificationWebpageGeneralProofDownloadDocumentAttrs,
} from 'interfaces/webpage/certification/registration/precondition'

const useCertificationGeneralProof = () => {
  // Hook
  const {
    certificationWebpageGeneralProof: {
      certificationWebpageGeneralProof_loading,
      certificationWebpageGeneralProof_list,
      certificationWebpageGeneralProof_CLEAR_LIST,
      certificationWebpageGeneralProof_MAP_ANSWER_FILE_LIST,
      certificationWebpageGeneralProof_fetchList: fetchList,
      certificationWebpageGeneralProof_update: update,
      certificationWebpageGeneralProof_uploadDocument: uploadDocument,
      certificationWebpageGeneralProof_deleteDocument: deleteDocument,
      certificationWebpageGeneralProof_downloadDocument: downloadDocument,
    },
  } = useStores()

  /**
   * @description Fetch general proof list
   *
   * @param {ICertificationWebpageGeneralProofGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageGeneralProof_fetchList = useCallback(
    (payload: ICertificationWebpageGeneralProofGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Update general proof
   *
   * @param {ICertificationWebpageGeneralProofUpdateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageGeneralProof_update = useCallback(
    (payload: ICertificationWebpageGeneralProofUpdateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  /**
   * @description Upload general proof document
   *
   * @param {ICertificationWebpageGeneralProofUploadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageGeneralProof_uploadDocument = useCallback(
    (payload: ICertificationWebpageGeneralProofUploadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        uploadDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [uploadDocument],
  )

  /**
   * @description Delete general proof document
   *
   * @param {ICertificationWebpageGeneralProofDeleteDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageGeneralProof_deleteDocument = useCallback(
    (payload: ICertificationWebpageGeneralProofDeleteDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        deleteDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [deleteDocument],
  )

  /**
   * @description Download document of general proof
   *
   * @param {ICertificationWebpageGeneralProofDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageGeneralProof_downloadDocument = useCallback(
    (payload: ICertificationWebpageGeneralProofDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [downloadDocument],
  )

  return {
    certificationWebpageGeneralProof_loading,
    certificationWebpageGeneralProof_list,
    certificationWebpageGeneralProof_CLEAR_LIST,
    certificationWebpageGeneralProof_MAP_ANSWER_FILE_LIST,
    certificationWebpageGeneralProof_fetchList,
    certificationWebpageGeneralProof_update,
    certificationWebpageGeneralProof_uploadDocument,
    certificationWebpageGeneralProof_deleteDocument,
    certificationWebpageGeneralProof_downloadDocument,
  }
}

export { useCertificationGeneralProof }
